import React, { useState, useEffect } from 'react';
import { type LayerConfig } from '../../../types/metadata';
import { useMapStore } from '../store/useMapStore';
import { createSvgIcon, wrapIconifyPath } from '../utils/svgUtils';
import { PointSettings } from '../../../types/metadata';

interface MarkerLayerConfigProps {
  layer: LayerConfig;
  settings: PointSettings;
}

type MarkerType = 'dot' | 'icon' | 'raw';

interface IconifyIcon {
  icon: string;
  name: string;
}

const rgbToHex = (rgb: number[]): string => {
  return '#' + rgb.map(x => {
    const hex = x.toString(16).toUpperCase();
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
};

const hexToRgb = (hex: string): [number, number, number] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : [255, 255, 255];
};

export const MarkerLayerConfig: React.FC<MarkerLayerConfigProps> = ({ layer, settings }) => {
  const updatePointSettings = useMapStore((state) => state.updatePointSettings);
  
  const getInitialMarkerType = (): MarkerType => {
    if (settings.markerType) return settings.markerType;
    if (!settings.svg) return 'dot';
    return 'icon';
  };
  
  const [markerType, setMarkerType] = useState<MarkerType>(getInitialMarkerType());
  const [iconSearch, setIconSearch] = useState('');
  const [iconResults, setIconResults] = useState<IconifyIcon[]>([]);
  const [selectedIconName, setSelectedIconName] = useState<string>('');
  const [isSearching, setIsSearching] = useState(false);
  const [rawSvg, setRawSvg] = useState(settings.svg || '');

  const handlePrimaryColorChange = (hex: string) => {
    const rgb = hexToRgb(hex);
    updatePointSettings(layer.id, { color: rgb });
  };

  const handleSecondaryColorChange = (hex: string) => {
    const rgb = hexToRgb(hex);
    updatePointSettings(layer.id, { borderColor: rgb });
  };

  const handleSizeChange = (value: number) => {
    updatePointSettings(layer.id, { size: value });
  };

  const handleStrokeChange = (value: number) => {
    updatePointSettings(layer.id, { borderSize: value });
  };

  const handleOpacityChange = (value: number) => {
    updatePointSettings(layer.id, { opacity: value });
  };

  const handleMarkerTypeChange = (type: MarkerType) => {
    setMarkerType(type);
    updatePointSettings(layer.id, { markerType: type });
    if (type === 'raw') {
      setRawSvg(settings.svg || '');
    }
  };

  const handleIconSearch = async (query: string) => {
    setIconSearch(query);
    if (!query.trim()) {
      setIconResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(`https://api.iconify.design/search?query=${encodeURIComponent(query)}&limit=50`);
      const data = await response.json();
      
      if (data.icons) {
        // Group icons by collection so we can batch-validate
        const byCollection: Record<string, string[]> = {};
        for (const fullName of data.icons as string[]) {
          const [collection, icon] = fullName.split(':');
          if (!collection || !icon) continue;
          if (!byCollection[collection]) byCollection[collection] = [];
          byCollection[collection].push(icon);
        }

        // Fetch each collection's data and keep only icons with a valid body
        const validIcons: IconifyIcon[] = [];
        await Promise.all(
          Object.entries(byCollection).map(async ([collection, icons]) => {
            try {
              const res = await fetch(
                `https://api.iconify.design/${collection}.json?icons=${icons.join(',')}`
              );
              const collectionData = await res.json();
              for (const icon of icons) {
                if (collectionData.icons?.[icon]?.body) {
                  validIcons.push({ icon: `${collection}:${icon}`, name: icon });
                }
              }
            } catch {
              // skip entire collection on network error
            }
          })
        );

        setIconResults(validIcons);
      }
    } catch (error) {
      console.error('Failed to search icons:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleIconSelect = async (iconName: string) => {
    try {
      const [collection, icon] = iconName.split(':');
      
      const response = await fetch(`https://api.iconify.design/${collection}.json?icons=${icon}`);
      const data = await response.json();
      
      if (!data.icons || !data.icons[icon]) {
        console.warn(`Icon "${iconName}" not found in Iconify response`);
        return;
      }
      
      const iconData = {
        body: data.icons[icon].body,
        width: data.icons[icon].width || data.width || 16,
        height: data.icons[icon].height || data.height || 16
      };
      
      const wrappedSvg = wrapIconifyPath(iconData);
      const processedSvg = wrappedSvg.replace(/currentColor/g, '{{color}}');
      
      updatePointSettings(layer.id, { svg: processedSvg });
      setSelectedIconName(iconName);
    } catch (error) {
      console.error('Failed to fetch icon data:', error);
    }
  };

  const handleRawSvgChange = (svg: string) => {
    setRawSvg(svg);
  };

  const handleRawSvgBlur = () => {
    updatePointSettings(layer.id, { svg: rawSvg || undefined });
  };

  useEffect(() => {
    if (markerType === 'raw') {
      setRawSvg(settings.svg || '');
    }
  }, [settings.svg, markerType]);

  return (
    <>
      <div className="marker-type-section">
        <h3>Marker Type</h3>
        <div className="marker-type-options">
          <label className="marker-type-option">
            <input
              type="radio"
              name="markerType"
              value="dot"
              checked={markerType === 'dot'}
              onChange={(e) => handleMarkerTypeChange(e.target.value as MarkerType)}
            />
            <span>Dot</span>
          </label>
          <label className="marker-type-option">
            <input
              type="radio"
              name="markerType"
              value="icon"
              checked={markerType === 'icon'}
              onChange={(e) => handleMarkerTypeChange(e.target.value as MarkerType)}
            />
            <span>Icon</span>
          </label>
          <label className="marker-type-option">
            <input
              type="radio"
              name="markerType"
              value="raw"
              checked={markerType === 'raw'}
              onChange={(e) => handleMarkerTypeChange(e.target.value as MarkerType)}
            />
            <span>Raw</span>
          </label>
        </div>
      </div>

      <hr className="section-divider" />

      <div className="marker-settings-content">
        <div className="preview-and-colors">
          <div className="marker-preview-box">
            {markerType !== 'dot' && settings.svg ? (
              <div 
                className="marker-preview-icon"
                style={{ 
                  width: `${settings.size! * 8}px`,
                  height: `${settings.size! * 8}px`,
                  opacity: settings.opacity ?? 1
                }}
                dangerouslySetInnerHTML={{ 
                  __html: createSvgIcon({
                    svg: settings.svg,
                    color: settings.color!,
                    size: 100,
                    backgroundColor: settings.borderColor!,
                  })
                }}
              />
            ) : (
              <div 
                className="marker-preview-circle"
                style={{ 
                  backgroundColor: `rgb(${settings.color![0]}, ${settings.color![1]}, ${settings.color![2]})`,
                  width: `${settings.size! * 3 * 2}px`,
                  height: `${settings.size! * 3 * 2}px`,
                  border: settings.borderSize! > 0 && settings.borderColor 
                    ? `${settings.borderSize! * (settings.size!/5) * 1.5}px solid rgb(${settings.borderColor[0]}, ${settings.borderColor[1]}, ${settings.borderColor[2]})` 
                    : 'none',
                  opacity: settings.opacity ?? 1
                }}
              />
            )}
          </div>
          
          <div className="color-inputs">
            <div className="color-input-group">
              <label>Primary:</label>
              <div className="color-input-wrapper">
                <input
                  type="color"
                  value={rgbToHex(settings.color!)}
                  onChange={(e) => handlePrimaryColorChange(e.target.value)}
                  className="color-picker-input"
                />
                <input
                  type="text"
                  value={rgbToHex(settings.color!)}
                  onChange={(e) => handlePrimaryColorChange(e.target.value)}
                  className="hex-input"
                  maxLength={7}
                />
              </div>
            </div>
            
            <div className={`color-input-group ${settings.borderSize === 0 ? 'disabled' : ''}`}>
              <label>Secondary:</label>
              <div className="color-input-wrapper">
                <input
                  type="color"
                  value={settings.borderColor ? rgbToHex(settings.borderColor) : '#FFFFFF'}
                  onChange={(e) => handleSecondaryColorChange(e.target.value)}
                  className="color-picker-input"
                  disabled={settings.borderSize === 0}
                />
                <input
                  type="text"
                  value={settings.borderColor ? rgbToHex(settings.borderColor) : '#FFFFFF'}
                  onChange={(e) => handleSecondaryColorChange(e.target.value)}
                  className="hex-input"
                  maxLength={7}
                  disabled={settings.borderSize === 0}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="slider-section">
          <div className="slider-row">
            <label>Size:</label>
            <input
              type="range"
              min="1"
              max="5"
              step="1"
              value={settings.size}
              onChange={(e) => handleSizeChange(Number(e.target.value))}
              className="slider"
            />
            <span className="slider-value">
              {['Mini', 'Small', 'Medium', 'Large', 'Huge'][settings.size! - 1]}
            </span>
          </div>
          
          {markerType === 'dot' && (
            <div className="slider-row">
              <label>Stroke:</label>
              <input
                type="range"
                min="0"
                max="3"
                step="1"
                value={settings.borderSize}
                onChange={(e) => handleStrokeChange(Number(e.target.value))}
                className="slider"
              />
              <span className="slider-value">
                {['None', 'Small', 'Medium', 'Large'][settings.borderSize!]}
              </span>
            </div>
          )}

          <div className="slider-row">
            <label>Opacity:</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={settings.opacity ?? 1}
              onChange={(e) => handleOpacityChange(Number(e.target.value))}
              className="slider"
            />
            <span className="slider-value">
              {Math.round((settings.opacity ?? 1) * 100)}%
            </span>
          </div>
        </div>

        {markerType === 'icon' && (
          <div className="icon-search-section">
            <input
              type="text"
              placeholder="Search icons..."
              value={iconSearch}
              onChange={(e) => handleIconSearch(e.target.value)}
              className="icon-search-input"
            />
            {isSearching && <div className="icon-loading">Searching...</div>}
            {iconResults.length > 0 && (
              <div className="icon-results">
                {iconResults.map((result) => (
                  <button
                    key={result.icon}
                    className={`icon-result-item ${selectedIconName === result.icon ? 'selected' : ''}`}
                    onClick={() => handleIconSelect(result.icon)}
                    title={result.icon}
                  >
                    <span className="iconify" data-icon={result.icon}></span>
                    <span className="icon-name">{result.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {markerType === 'raw' && (
          <div className="raw-svg-section">
            <label>SVG Code:</label>
            <textarea
              value={rawSvg}
              onChange={(e) => handleRawSvgChange(e.target.value)}
              onBlur={handleRawSvgBlur}
              className="raw-svg-textarea"
              placeholder="Enter raw SVG code..."
              rows={3}
              style={{ resize: 'vertical' }}
            />
          </div>
        )}
      </div>
    </>
  );
};
